import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { V } from "@/lib/vocab";
import { Home, RefreshCw, UserCheck } from "lucide-react";

// Inline prompts under the invite form (iter475e): "they're already an owner here — link them" and
// "already signs in as X — change their role to Y?". Looks people up as the email/phone settles,
// and also accepts the 409 payload when someone hits Send before the lookup returned.
export function InviteMatchPanel({ email, phone, role, roleLabel, companyId, tenantIds, clash, value, onChange }) {
  const [info, setInfo] = useState(null);
  const key = `${(email || "").trim().toLowerCase()}|${(phone || "").replace(/\D/g, "")}|${role}|${companyId || ""}|${(tenantIds || []).join(",")}`;

  useEffect(() => {
    const e = (email || "").trim(), p = (phone || "").replace(/\D/g, "");
    if ((!e || !e.includes("@")) && p.length < 10) { setInfo(null); return; }
    let live = true;
    const t = setTimeout(() => {
      api.get("/admins/invite/lookup", { params: { email: e.includes("@") ? e : "", phone: p.length >= 10 ? p : "", role,
        company_id: companyId || "", tenant_ids: (tenantIds || []).join(",") } })
        .then((r) => live && setInfo(r.data)).catch(() => live && setInfo(null));
    }, 450);
    return () => { live = false; clearTimeout(t); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const existing = clash?.existing_user
    ? { ...clash.existing_user, role_label: clash.existing_user.role_label || clash.message, can_change_role: !!clash.can_change_role }
    : info?.existing_user;
  const roster = info?.roster_match;
  if (!existing && !roster) return null;

  return (
    <div className="mt-4 space-y-2" data-testid="invite-match-panel">
      {roster && (
        <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 cursor-pointer" data-testid="invite-roster-match">
          <input type="checkbox" data-testid="invite-link-roster" className="mt-1 h-4 w-4 accent-emerald-600"
            checked={value.link_roster !== false} onChange={(e) => onChange({ ...value, link_roster: e.target.checked })} />
          <span className="text-sm text-emerald-900">
            <span className="font-semibold inline-flex items-center gap-1.5"><Home className="h-3.5 w-3.5" /> {roster.name || "This person"} is also {roster.relationship ? `${/^[aeiou]/i.test(roster.relationship) ? "an" : "a"} ${roster.relationship.toLowerCase()}` : V.a("person")} at {roster.tenant_name}{roster.unit ? ` (${roster.unit})` : ""}.</span>
            <span className="block text-xs text-emerald-800/80 mt-0.5">Link this login to that record — one identity: the concierge knows them on both sides, and they can open their portal from Account. Untick to keep them separate.</span>
          </span>
        </label>
      )}
      {existing && !existing.same_role && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="invite-existing-user">
          <div className="text-sm text-amber-900 font-semibold inline-flex items-center gap-1.5">
            <UserCheck className="h-3.5 w-3.5" /> {existing.name || "This person"} already signs in as {existing.role_label?.replace(/^(a|an) /, "") || existing.role}.
          </div>
          {existing.can_change_role ? (
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="invite-change-role" className="mt-1 h-4 w-4 accent-amber-600"
                checked={!!value.change_role} onChange={(e) => onChange({ ...value, change_role: e.target.checked })} />
              <span className="text-sm text-amber-900">
                <span className="font-semibold inline-flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Change their role to {roleLabel}</span>
                <span className="block text-xs text-amber-800/80 mt-0.5">Sending the invite re-seats them — same login, new access, fresh sign-in link. Nothing to re-create.</span>
              </span>
            </label>
          ) : (
            <p className="text-xs text-amber-800/80">Only a super admin or their firm's Administrator can change this seat. Ask them, or adjust it from the Team page.</p>
          )}
        </div>
      )}
      {existing && existing.same_role && (
        <p className="text-xs text-ink/60" data-testid="invite-existing-same-role">{existing.name || "This person"} is already on the team as {roleLabel} — sending adds access and a fresh sign-in link.</p>
      )}
    </div>
  );
}
